import { useState } from "react";
import { Share2, Twitter, Linkedin, Facebook, Link2, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";

const BRAND_SOCIAL = [
  { icon: Instagram, href: "https://instagram.com/peoplewhomatter.media", label: "Instagram" },
  { icon: Linkedin, href: "https://linkedin.com/company/people-who-matter", label: "LinkedIn" },
  { icon: Facebook, href: "https://facebook.com/peoplewhomattermedia", label: "Facebook" },
  { icon: Youtube, href: "https://youtube.com/@PeopleWhoMatterMedia", label: "YouTube" },
];

/**
 * Share + brand social bar reused on profile / article pages.
 * Renders the Share dropdown alongside the brand's social channels.
 *
 * Props:
 *   title  – content title used in share message
 *   align  – "between" (default) or "center"
 */
const ArticleShareRow = ({ title = "Check this out", align = "between" }) => {
  const [open, setOpen] = useState(false);

  const share = (network) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(title);
    let target = "";
    if (network === "twitter") target = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    else if (network === "linkedin") target = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    else if (network === "facebook") target = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    else if (network === "copy") {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
      setOpen(false);
      return;
    }
    if (target) window.open(target, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-3 ${align === "center" ? "justify-center" : "justify-between"}`}
      data-testid="article-share-row"
    >
      {/* Share button + dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium hover:bg-neutral-50 transition-colors"
          style={{ border: "1px solid rgba(10,10,10,0.12)" }}
          data-testid="share-button"
        >
          <Share2 className="w-4 h-4" /> Share
        </button>
        {open && (
          <div className="absolute left-0 top-full mt-2 w-44 bg-white shadow-lg z-20" style={{ border: "1px solid rgba(10,10,10,0.08)" }} data-testid="share-dropdown">
            <button onClick={() => share("twitter")} className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-neutral-50"><Twitter className="w-4 h-4" /> Twitter</button>
            <button onClick={() => share("linkedin")} className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-neutral-50"><Linkedin className="w-4 h-4" /> LinkedIn</button>
            <button onClick={() => share("facebook")} className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-neutral-50"><Facebook className="w-4 h-4" /> Facebook</button>
            <button onClick={() => share("copy")} className="flex items-center gap-3 w-full px-4 py-2 text-sm hover:bg-neutral-50"><Link2 className="w-4 h-4" /> Copy Link</button>
          </div>
        )}
      </div>

      {/* Brand socials */}
      <div className="flex items-center gap-2">
        <span className="hidden sm:inline text-xs text-neutral-500 mr-1">Follow us</span>
        {BRAND_SOCIAL.map((s) => (
          <a
            key={s.label}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            className="w-9 h-9 inline-flex items-center justify-center text-neutral-700 hover:bg-neutral-950 hover:text-white transition-colors"
            style={{ border: "1px solid rgba(10,10,10,0.12)" }}
            aria-label={s.label}
            data-testid={`brand-social-${s.label.toLowerCase()}`}
          >
            <s.icon className="w-4 h-4" />
          </a>
        ))}
      </div>
    </div>
  );
};

export default ArticleShareRow;
